import React, { useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import GalleryComponentEditor from './components/GalleryComponentEditor';

// Editor for a page components collection shaped as:
// { items: [ { type: string, positionSmallScreen?: 'top'|'bottom'|'hidden', positionWideScreen?: 'top'|'bottom'|'hidden'|'right'|'left', images?: [ { src: string, title?: string } ] } ] }
// Props:
// - value: current object (optional)
// - onChange: function(nextValue)
// - websiteId: required by ImageUploader
// - className?: optional css class

function normalize(value) {
  const base = value && typeof value === 'object' ? value : {};
  const items = Array.isArray(base.items) ? base.items : [];
  const allowedSmall = ['top', 'bottom', 'hidden'];
  const allowedWide = ['top', 'bottom', 'hidden', 'right', 'left'];
  const normItems = items.map((it) => {
    const type = String(it?.type || '');
    // Use camelCase only (no backward-compat)
    const psCandidate = it?.positionSmallScreen || 'hidden';
    const pwCandidate = it?.positionWideScreen || 'hidden';
    const psRaw = String(psCandidate);
    const pwRaw = String(pwCandidate);
    const positionSmallScreen = allowedSmall.includes(psRaw) ? psRaw : 'hidden';
    const positionWideScreen = allowedWide.includes(pwRaw) ? pwRaw : 'hidden';
    const images = Array.isArray(it?.images)
      ? it.images.map((img) => ({ src: String(img?.src || ''), title: img?.title ? String(img.title) : '' }))
      : [];
    const slideIntervalSeconds = it?.slideIntervalSeconds !== undefined ? Number(it.slideIntervalSeconds) : 3;
    const autoPlay = it?.autoPlay === undefined ? true : !!it.autoPlay;
    const showNav = !!it?.showNav;
    const showFullScreenButton = it?.showFullScreenButton === undefined ? true : !!it.showFullScreenButton;
    const showThumbnails = !!it?.showThumbnails;
    const showImageTitles = !!it?.showImageTitles;
    return { type, positionSmallScreen, positionWideScreen, images, slideIntervalSeconds, autoPlay, showNav, showFullScreenButton, showThumbnails, showImageTitles };
  });
  return { items: normItems };
}

const emptyItem = {
  type: 'gallery',
  positionSmallScreen: 'hidden',
  positionWideScreen: 'hidden',
  images: [],
  slideIntervalSeconds: 3,
  autoPlay: true,
  showNav: false,
  showFullScreenButton: true,
  showThumbnails: false,
  showImageTitles: false,
};

const SMALL_OPTIONS = [
  { value: 'hidden', label: '–', title: 'Hidden' },
  { value: 'top',    label: '↑', title: 'Top' },
  { value: 'bottom', label: '↓', title: 'Bottom' },
];

const WIDE_OPTIONS = [
  { value: 'hidden', label: '–', title: 'Hidden' },
  { value: 'top',    label: '↑', title: 'Top' },
  { value: 'bottom', label: '↓', title: 'Bottom' },
  { value: 'right',  label: '→', title: 'Right' },
  { value: 'left',   label: '←', title: 'Left' },
];

function PosControl({ options, value, onChange }) {
  return (
    <div className="pos-control">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`pos-btn${value === opt.value ? ' pos-btn--active' : ''}`}
          onClick={() => onChange(opt.value)}
          title={opt.title}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function ComponentsCollectionEditor({ value, onChange, websiteId, className = '' }) {
  const data = useMemo(() => normalize(value), [value]);

  const emit = (next) => onChange?.(normalize(next));

  const addItem = () => emit({ items: [...data.items, { ...emptyItem }] });
  const removeItem = (idx) => emit({ items: data.items.filter((_, i) => i !== idx) });
  const moveItem = (idx, dir) => {
    const arr = [...data.items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[j];
    arr[j] = tmp;
    emit({ items: arr });
  };
  const updateItemField = (idx, field, val) => {
    const arr = data.items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    emit({ items: arr });
  };

  return (
    <div className={className}>
      <div className="mb-3">
        <Button size="sm" className="primary-button" onClick={addItem}>+ Add</Button>
      </div>

      {data.items.length === 0 && (
        <div className="muted-hint mb-2">No components added yet</div>
      )}

      {data.items.map((it, idx) => (
        <div key={idx} className="component-item">
          <div className="component-item-header">
            <Form.Group className="component-item-field--wide">
              <Form.Label className="component-item-field-label">Type</Form.Label>
              <Form.Select size="sm" value={it.type} onChange={(e) => updateItemField(idx, 'type', e.target.value)}>
                <option value="gallery">gallery</option>
                <option value="custom">custom</option>
              </Form.Select>
            </Form.Group>

            <div className="component-item-pos-group">
              <span className="component-item-pos-label">Mobile</span>
              <PosControl
                options={SMALL_OPTIONS}
                value={it.positionSmallScreen}
                onChange={(val) => updateItemField(idx, 'positionSmallScreen', val)}
              />
            </div>

            <div className="component-item-pos-group">
              <span className="component-item-pos-label">Desktop</span>
              <PosControl
                options={WIDE_OPTIONS}
                value={it.positionWideScreen}
                onChange={(val) => updateItemField(idx, 'positionWideScreen', val)}
              />
            </div>

            <div className="component-item-controls">
              <button
                type="button"
                className="icon-btn"
                onClick={() => moveItem(idx, -1)}
                disabled={idx === 0}
                title="Move up"
              >↑</button>
              <button
                type="button"
                className="icon-btn"
                onClick={() => moveItem(idx, 1)}
                disabled={idx === data.items.length - 1}
                title="Move down"
              >↓</button>
              <button
                type="button"
                className="structure-delete-btn"
                onClick={() => removeItem(idx)}
                title="Remove component"
              >×</button>
            </div>
          </div>

          {it.type === 'gallery' && (
            <div className="mt-2">
              <GalleryComponentEditor
                item={it}
                websiteId={websiteId}
                onChange={(nextItem) => {
                  const arr = data.items.map((item, i) => (i === idx ? nextItem : item));
                  emit({ items: arr });
                }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
